import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetIdentity, useLogout } from "../../../contexts/AuthContext";
import { useTheme } from "../../../contexts/ThemeContext";
import { Layout, Input, Breadcrumb } from "antd";
import {
  SunOutlined,
  MoonOutlined,
  SearchOutlined,
  HomeOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MenuOutlined,
} from "@ant-design/icons";
import { AdminNotificationBell } from "../../notifications/AdminNotificationBell";
import { CohortSelector } from "./CohortSelector";

const { Header: AntdHeader } = Layout;

// Route to breadcrumb label mapping
const routeLabels: Record<string, string> = {
  dashboard: "Dashboard",
  applications: "Applications",
  ApplicationStats: "Application Stats",
  interviews: "Interviews",
  review: "Review",
  "interview-queue": "Interview Queue",
  Admissions: "Admission Decisions",
  "prospective-students": "Prospective Students",
  Students: "Students",
  Deferred: "Deferred Students",
  StudentStats: "Student Stats",
  RedlistedStudents: "Redlisted Students",
  internships: "Internships",
  Programs: "Programs",
  cohorts: "Cohorts",
  intakes: "Intakes",
  "student-ledger": "Student Ledger",
  courses: "Courses",
  certificates: "Certificates",
  QuizDashboard: "Quizzes",
  Schedules: "Task & Quiz Library",
  grading: "Grading",
  TaskManager: "Tasks",
  Curriculum: "Curriculum",
  "practicals-library": "Practicals Library",
  "library-suggestions": "Suggested edits",
  "class-deliveries": "Class deliveries",
  gear: "Gear",
  presentations: "Presentations",
  attendance: "Attendance",
  sessions: "Sessions",
  students: "Students",
  rules: "Rules",
  session: "Take attendance",
  finance: "Finance Overview",
  Fees: "Fees",
  transactions: "Transactions",
  Audit: "Audit",
  "financial-statistics": "Finance Stats",
  "debt-collection": "Debt Collection",
  "loan-manager": "Loan Manager",
  Tickets: "Support Tickets",
  events: "Events",
  MarketingDash: "Marketing Overview",
  LeadManager: "Lead Manager",
  referralmanagement: "Referrals",
  Templates: "Templates",
  Sender: "Sender",
  Sequences: "Sequences",
  EmailInbox: "Email Inbox",
  WhatsAppInbox: "WhatsApp Inbox",
  Notifications: "Notification Feed",
  NotificationSettings: "Notification Settings",
  Users: "Users",
  settings: "Settings",
};

interface HeaderProps {
  collapsed: boolean;
  setCollapsed: (val: boolean) => void;
  mobileDrawerOpen: boolean;
  onOpenMobileDrawer: () => void;
}

export const Header: React.FC<HeaderProps> = ({ collapsed, setCollapsed, mobileDrawerOpen, onOpenMobileDrawer }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const path = location.pathname.toLowerCase();
  const isGlobalCommunicationRoute = [
    "/admin/emailinbox",
    "/admin/whatsappinbox",
    "/admin/sender",
    "/admin/campaign",
  ].some((prefix) => path.startsWith(prefix));

  // Generate breadcrumb items from current path
  const getBreadcrumbItems = () => {
    const pathParts = location.pathname.split("/").filter(Boolean);

    const items = [
      {
        key: "home",
        title: (
          <span
            onClick={() => navigate("/admin/dashboard")}
            style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
          >
            <HomeOutlined /> Home
          </span>
        ),
      },
    ];

    let currentPath = "";
    pathParts.forEach((part, index) => {
      if (part === "admin") return;

      currentPath += `/${part}`;
      const label = routeLabels[part] || part.charAt(0).toUpperCase() + part.slice(1);

      items.push({
        key: part,
        title:
          index === pathParts.length - 1 ? (
            <span>{label}</span>
          ) : (
            <span
              onClick={() => navigate(`/admin${currentPath}`)}
              style={{ cursor: "pointer" }}
            >
              {label}
            </span>
          ),
      });
    });

    return items;
  };

  return (
    <AntdHeader className="admin-header">
      {/* Left Section */}
      <div className="admin-header-left">
        {/* Mobile menu trigger - opens sidebar drawer */}
        <button
          type="button"
          onClick={onOpenMobileDrawer}
          className="admin-mobile-menu-button"
          aria-label="Open menu"
          aria-expanded={mobileDrawerOpen}
        >
          <MenuOutlined style={{ fontSize: 18 }} />
        </button>

        {/* Desktop sidebar collapse trigger */}
        <div
          onClick={() => setCollapsed(!collapsed)}
          style={{
            cursor: 'pointer',
            fontSize: '18px',
            color: 'var(--dyen-text)',
            padding: '4px',
            borderRadius: '4px',
            transition: 'background 0.2s',
            marginRight: '8px'
          }}
          className="sidebar-toggle-btn hidden md:flex items-center"
          title={collapsed ? "Expand Sidebar" : "Collapse Sidebar"}
        >
          {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        </div>

        <Breadcrumb
          className="admin-breadcrumb hidden sm:flex"
          items={getBreadcrumbItems()}
        />
      </div>

      {/* Right Section - Search, Theme Toggle, Notifications */}
      <div className="admin-header-right">
        {!isGlobalCommunicationRoute && (
          <>
            <div className="block lg:hidden">
              <CohortSelector compact />
            </div>
            <div className="hidden lg:block">
              <CohortSelector />
            </div>
          </>
        )}
        {/* Search - hidden on mobile */}
        <Input
          className="header-search hidden xl:flex"
          placeholder="Search..."
          prefix={<SearchOutlined />}
          allowClear
        />

        {/* Theme Toggle */}
        <button className="theme-toggle" onClick={toggleTheme} title={isDark ? "Switch to light mode" : "Switch to dark mode"}>
          {isDark ? <SunOutlined /> : <MoonOutlined />}
        </button>

        {/* Notifications */}
        <AdminNotificationBell />
      </div>
    </AntdHeader>
  );
};

export default Header;
